import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Loader,
  NumberInput,
  Select,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useCreateFundCall,
  useSendFundCall,
  useUpdateFundCall,
} from 'src/Hooks/Mutations/useFundCallMutations';
import { useFundCall } from 'src/Hooks/Queries/useFundCalls';
import {
  CreateFundCallDtoFractionEnum,
  CreateFundCallDtoScopeEnum,
  CreateFundCallDtoTypeEnum,
  FundCallDto,
  FundCallDtoStatusEnum,
  SettlementTargetDtoTypeEnum,
} from 'src/Services/API/generated';
import { trackFundCallCreated, trackFundCallSent } from 'src/Services/Mixpanel/events';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './FundCallWizard.module.scss';
import { Props } from './FundCallWizard.types';

/** Le brouillon existe dès l'étape 2 : c'est lui qui porte les lignes calculées par l'API. */
const FundCallWizard = ({ fundCallId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<FundCallDto | null>(null);

  // Preset from URL (e.g. ?scope=SELF&type=STATEMENT) so entry points like
  // « Envoyer mon relevé » can open the wizard pre-configured. Read once at mount.
  const [type, setType] = useState<CreateFundCallDtoTypeEnum>(() =>
    searchParams.get('type') === CreateFundCallDtoTypeEnum.FundCall
      ? CreateFundCallDtoTypeEnum.FundCall
      : CreateFundCallDtoTypeEnum.Statement
  );
  const [scope] = useState<CreateFundCallDtoScopeEnum>(() =>
    searchParams.get('scope') === CreateFundCallDtoScopeEnum.Self
      ? CreateFundCallDtoScopeEnum.Self
      : CreateFundCallDtoScopeEnum.All
  );
  const [fraction, setFraction] = useState<CreateFundCallDtoFractionEnum>(
    CreateFundCallDtoFractionEnum.Half
  );
  const [periodLabel, setPeriodLabel] = useState('');
  const [includeCurrentBalance, setIncludeCurrentBalance] = useState(false);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [message, setMessage] = useState('');
  // Prefilled by the API with the asset's last choice; the PDF stays individual by default.
  const [includeStatement, setIncludeStatement] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);
  const [showUpToDate, setShowUpToDate] = useState(false);

  // « Modifier » sur un brouillon : on reprend ses lignes déjà calculées, à l'étape des
  // montants. Le type et la fraction ne se changent plus — ils ont produit ces lignes.
  const isEditing = !!fundCallId;
  const { data: existingDraft, isError: existingDraftFailed } = useFundCall(fundCallId);
  useEffect(() => {
    if (!existingDraft || draft) return;
    if (existingDraft.status !== FundCallDtoStatusEnum.Draft) {
      navigate(`../fund-calls/${existingDraft.id}`, { replace: true });
      return;
    }
    setDraft(existingDraft);
    // Mêmes valeurs côté lecture et création, seuls les types générés diffèrent.
    setType(existingDraft.type as CreateFundCallDtoTypeEnum);
    setFraction(existingDraft.fraction as CreateFundCallDtoFractionEnum);
    setIncludeCurrentBalance(existingDraft.includeCurrentBalance);
    setPeriodLabel(existingDraft.periodLabel);
    setDueDate(existingDraft.dueDate ? dayjs(existingDraft.dueDate).toDate() : null);
    setMessage(existingDraft.message ?? '');
    setIncludeStatement(existingDraft.includeStatement);
    setStep(1);
  }, [existingDraft, draft, navigate]);

  const createFundCall = useCreateFundCall();
  const updateFundCall = useUpdateFundCall();
  const sendFundCall = useSendFundCall();

  const isFundCall = type === CreateFundCallDtoTypeEnum.FundCall;

  /**
   * Vocabulaire de l'État des comptes, le même que sur le détail d'un appel. Convention de
   * l'équilibre : positif = la maison lui doit. Jamais de montant négatif à l'écran.
   */
  const balanceLabel = (currentBalance: number): string => {
    if (currentBalance > 0) {
      return t('fundCalls.wizard.owedToThem', 'On lui doit {{amount}}', {
        amount: formatEuros(currentBalance),
      });
    }
    if (currentBalance < 0) {
      return t('fundCalls.wizard.owes', 'Doit {{amount}}', {
        amount: formatEuros(Math.abs(currentBalance)),
      });
    }
    return t('fundCalls.wizard.upToDate', 'À jour');
  };

  const stepTitles = [
    t('fundCalls.wizard.step1', 'Type et période'),
    t('fundCalls.wizard.step2', 'Montants'),
    t('fundCalls.wizard.step3', 'Message et envoi'),
  ];

  const stepSubtitles = [
    t('fundCalls.wizard.step1Help', 'Ce que vous appelez, sur quelle période, pour quand.'),
    t(
      'fundCalls.wizard.step2Help',
      'Montants calculés depuis les soldes. Ajustez-les si besoin, décochez qui ne doit pas être appelé.'
    ),
    t('fundCalls.wizard.step3Help', 'Le message accompagne chaque relevé envoyé.'),
  ];

  const goToAmounts = () => {
    if (!periodLabel.trim()) {
      // Jamais de bouton muet : le motif est affiché sous le champ concerné.
      setPeriodError(
        t('fundCalls.wizard.periodRequired', 'Nommez la période, elle sert de titre.')
      );
      return;
    }
    setPeriodError(null);

    if (draft) {
      // Brouillon déjà créé (retour à l'étape 1, ou « Modifier ») : on ne recrée pas un second
      // appel, on corrige celui-ci.
      updateFundCall.mutate(
        {
          id: draft.id,
          dto: {
            periodLabel: periodLabel.trim(),
            dueDate: dueDate ? dueDate.toISOString() : undefined,
          },
        },
        { onSuccess: () => setStep(1) }
      );
      return;
    }

    createFundCall.mutate(
      {
        type,
        scope,
        periodLabel: periodLabel.trim(),
        fraction,
        includeCurrentBalance,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
      },
      {
        onSuccess: fundCall => {
          setDraft(fundCall);
          setMessage(fundCall.message ?? '');
          setIncludeStatement(fundCall.includeStatement);
          trackFundCallCreated({ type, lines_count: fundCall.lines.length });
          setStep(1);
        },
      }
    );
  };

  const patchLine = (lineId: string, amountCalled: number) => {
    if (!draft) return;
    setDraft({
      ...draft,
      lines: draft.lines.map(line => (line.id === lineId ? { ...line, amountCalled } : line)),
    });
  };

  const toggleLine = (lineId: string, excluded: boolean) => {
    if (!draft) return;
    setDraft({
      ...draft,
      lines: draft.lines.map(line =>
        line.id === lineId ? { ...line, status: excluded ? 'EXCLUDED' : 'PENDING' } : line
      ),
    });
  };

  const saveAndGoToMessage = () => {
    if (!draft) return;
    updateFundCall.mutate(
      {
        id: draft.id,
        dto: {
          lines: draft.lines.map(line => ({
            id: line.id,
            amountCalled: line.amountCalled,
            excluded: line.status === 'EXCLUDED',
          })),
        },
      },
      { onSuccess: () => setStep(2) }
    );
  };

  const send = () => {
    if (!draft) return;
    updateFundCall.mutate(
      { id: draft.id, dto: { message, includeStatement } },
      {
        onSuccess: () => {
          sendFundCall.mutate(
            { id: draft.id, dto: { sendEmail: true, postToFeed: true } },
            {
              onSuccess: sent => {
                trackFundCallSent({
                  type,
                  lines_count: sent.calledCount,
                  include_statement: sent.includeStatement,
                });
                notifications.show({
                  message: t('fundCalls.wizard.sent', 'Appel de fonds envoyé.'),
                });
                navigate(`../fund-calls/${sent.id}`);
              },
            }
          );
        },
      }
    );
  };

  // Loader until the draft is copied into the state, not just until it is fetched: in between,
  // one render showed step 1 (and its buttons) before jumping to the amounts.
  if (isEditing && !draft) {
    return existingDraftFailed ? (
      <Alert color="red">
        {t('fundCalls.wizard.loadFailed', 'Ce brouillon n’a pas pu être chargé.')}
      </Alert>
    ) : (
      <Loader />
    );
  }

  // Relevé : les membres à jour n'ont rien à verser, on les replie pour que la liste montre
  // d'abord qui doit. En appel de quote-part, tout le monde reste visible : chacun est appelé.
  const isUpToDate = (line: FundCallDto['lines'][number]) =>
    !isFundCall && Math.abs(line.currentBalance) < 0.01;
  const upToDateCount = draft?.lines.filter(isUpToDate).length ?? 0;
  const visibleLines = (draft?.lines ?? []).filter(line => showUpToDate || !isUpToDate(line));

  return (
    <div className={styles.container}>
      <div className={styles.stepper}>
        {stepTitles.map((title, index) => (
          <button
            key={title}
            type="button"
            // On ne saute pas en avant : les étapes 2 et 3 n'existent qu'une fois le
            // brouillon créé, elles n'auraient rien à afficher.
            disabled={index > step}
            className={classnames(styles.stepPill, {
              [styles.stepPillActive]: index === step,
              [styles.stepPillDone]: index < step,
            })}
            onClick={() => setStep(index)}
          >
            <span className={styles.stepIndex}>{index < step ? '✓' : index + 1}</span>
            {title}
          </button>
        ))}
      </div>

      <div>
        <h3 className={styles.stepTitle}>{stepTitles[step]}</h3>
        <p className={styles.stepSubtitle}>{stepSubtitles[step]}</p>
      </div>

      {step === 0 && (
        <div className={styles.fields}>
          <Select
            label={t('fundCalls.wizard.type', 'Type')}
            data={[
              {
                value: CreateFundCallDtoTypeEnum.Statement,
                label: t('fundCalls.type.statement', 'Relevé de compte'),
              },
              {
                value: CreateFundCallDtoTypeEnum.FundCall,
                label: t('fundCalls.type.fundCall', 'Appel de fonds'),
              },
            ]}
            value={type}
            onChange={value => setType(value as CreateFundCallDtoTypeEnum)}
            allowDeselect={false}
            disabled={!!draft}
          />

          <TextInput
            label={t('fundCalls.wizard.period', 'Période')}
            placeholder={t('fundCalls.wizard.periodPlaceholder', 'ex. 2ᵉ semestre 2026')}
            value={periodLabel}
            onChange={event => setPeriodLabel(event.currentTarget.value)}
            error={periodError}
          />

          <DatePickerInput
            label={t('fundCalls.wizard.dueDate', 'Échéance')}
            placeholder={t('fundCalls.wizard.dueDatePlaceholder', 'Par défaut : dans 30 jours')}
            value={dueDate}
            onChange={setDueDate}
            clearable
          />

          {isFundCall && (
            <>
              <Select
                label={t('fundCalls.wizard.fraction', 'Fraction appelée')}
                data={[
                  {
                    value: CreateFundCallDtoFractionEnum.Year,
                    label: t('fundCalls.fraction.YEAR', 'Année'),
                  },
                  {
                    value: CreateFundCallDtoFractionEnum.Half,
                    label: t('fundCalls.fraction.HALF', 'Semestre'),
                  },
                  {
                    value: CreateFundCallDtoFractionEnum.Quarter,
                    label: t('fundCalls.fraction.QUARTER', 'Trimestre'),
                  },
                  {
                    value: CreateFundCallDtoFractionEnum.Month,
                    label: t('fundCalls.fraction.MONTH', 'Mois'),
                  },
                ]}
                value={fraction}
                onChange={value => setFraction(value as CreateFundCallDtoFractionEnum)}
                allowDeselect={false}
                disabled={!!draft}
              />
              <Checkbox
                label={t('fundCalls.wizard.includeBalance', 'Inclure le solde en cours')}
                disabled={!!draft}
                checked={includeCurrentBalance}
                onChange={event => setIncludeCurrentBalance(event.currentTarget.checked)}
              />
            </>
          )}

          <div className={styles.actions}>
            <Button variant="outline" onClick={() => navigate('..')}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={goToAmounts} loading={createFundCall.isLoading}>
              {t('fundCalls.wizard.next', 'Continuer')}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className={styles.fields}>
          {!draft ? (
            <Loader />
          ) : (
            <>
              <div className={styles.lines}>
                {visibleLines.map(line => (
                  <div key={line.id} className={styles.lineRow}>
                    <Checkbox
                      checked={line.status !== 'EXCLUDED'}
                      onChange={event => toggleLine(line.id, !event.currentTarget.checked)}
                      aria-label={line.party.displayName ?? ''}
                    />
                    <Avatar
                      src={line.party.profilePictureUrl}
                      color="initials"
                      name={line.party.displayName}
                      radius="xl"
                      size={36}
                    />
                    <div className={styles.lineName}>
                      {line.party.displayName}
                      {line.householdName && (
                        <div className={styles.lineMeta}>{line.householdName}</div>
                      )}
                      <div className={styles.lineMeta}>{balanceLabel(line.currentBalance)}</div>
                      {line.party.type === SettlementTargetDtoTypeEnum.PlaceholderMember && (
                        <div className={styles.lineNotice}>
                          {t(
                            'fundCalls.wizard.notRegistered',
                            'Non inscrit — à transmettre vous-même'
                          )}
                        </div>
                      )}
                    </div>
                    <NumberInput
                      className={styles.amountInput}
                      value={line.amountCalled}
                      onChange={value => patchLine(line.id, Number(value) || 0)}
                      min={0}
                      decimalScale={2}
                      decimalSeparator=","
                      thousandSeparator=" "
                      suffix=" €"
                      hideControls
                    />
                  </div>
                ))}
              </div>

              {upToDateCount > 0 && (
                <Button
                  variant="subtle"
                  className={styles.upToDateToggle}
                  onClick={() => setShowUpToDate(current => !current)}
                >
                  {showUpToDate
                    ? t('fundCalls.wizard.hideUpToDate', 'Masquer les membres à jour')
                    : t('fundCalls.wizard.showUpToDate', {
                        count: upToDateCount,
                        defaultValue: '{{count}} membre à jour — afficher',
                        defaultValue_other: '{{count}} membres à jour — afficher',
                      })}
                </Button>
              )}

              <div className={styles.actions}>
                <Button variant="outline" onClick={() => setStep(0)}>
                  {t('common.back', 'Retour')}
                </Button>
                <Button onClick={saveAndGoToMessage} loading={updateFundCall.isLoading}>
                  {t('fundCalls.wizard.next', 'Continuer')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div className={styles.fields}>
          <Textarea
            label={t('fundCalls.wizard.message', "Message d'accompagnement")}
            autosize
            minRows={6}
            value={message}
            onChange={event => setMessage(event.currentTarget.value)}
          />
          {scope === CreateFundCallDtoScopeEnum.All && (
            <Checkbox
              label={t(
                'fundCalls.wizard.includeStatement',
                'Joindre l’état des comptes de tous les membres'
              )}
              checked={includeStatement}
              onChange={event => setIncludeStatement(event.currentTarget.checked)}
            />
          )}
          <p className={styles.hint}>
            {t(
              'fundCalls.wizard.sendHint',
              'Chaque propriétaire reçoit son relevé par e-mail avec son PDF. Ceux dont l’adresse est inconnue sont signalés pour un envoi manuel.'
            )}
          </p>

          <div className={styles.actions}>
            <Button variant="outline" onClick={() => setStep(1)}>
              {t('common.back', 'Retour')}
            </Button>
            <Button onClick={send} loading={sendFundCall.isLoading || updateFundCall.isLoading}>
              {t('fundCalls.wizard.send', 'Envoyer')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundCallWizard;
